/**
 * P4 — N0 · Competência 1 (Numérico-aritmética) · task-model H2.4.07
 *
 *   "Calcular o valor após dois ou mais reajustes percentuais que incidem em cadeia."
 *
 * Dois caminhos do esquema estreiam aqui:
 *
 *   - **N0**: item sem nenhum estímulo. É o piso da taxonomia — a questão de prova
 *     comum, na tela — e existia no esquema sem nenhum item que o exercitasse.
 *   - **resposta numérica digitada**: sem alternativas. O aluno digita o valor, e
 *     mesmo assim recebe DIAGNÓSTICO NOMEADO, porque comparamos o que ele digitou
 *     com a saída de cada solucionador defeituoso. A ancoragem em erro não depende
 *     de haver múltipla escolha.
 *
 * Por isso o modelo de resposta é 2PL e não 3PL: sem cinco alternativas, não há
 * acerto ao acaso a estimar.
 */

import { decimalPlaces, formatBR, roundTo } from '../../core/num.ts';
import type { Rng } from '../../core/rng.ts';
import { comExplicacoes, type Explicacoes } from '../../engine/explain.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  ARRED_TRUNCA,
  PCT_MEDIA_DAS_TAXAS,
  PCT_SINAL_TROCADO,
  PCT_SOMA_TAXAS,
  PCT_SO_O_MAIOR,
  PCT_SO_O_PRIMEIRO,
  PCT_VARIACAO_POR_MONTANTE,
} from '../../schema/errors.ts';
import type { BuggySolver, ItemTemplate } from '../../schema/item.ts';
import { NOT_APPLICABLE, templateId } from '../../schema/item.ts';
import { capitalizar } from '../../core/pt.ts';
import { br, strong, txt } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

interface Cenario {
  readonly bem: string;
  readonly sujeito: string;
  readonly periodo1: string;
  readonly periodo2: string;
}

const CENARIOS: readonly Cenario[] = [
  {
    bem: 'o valor do aluguel',
    sujeito: 'de uma sala comercial',
    periodo1: 'no primeiro ano',
    periodo2: 'no ano seguinte',
  },
  {
    bem: 'a mensalidade',
    sujeito: 'de um curso técnico',
    periodo1: 'em janeiro',
    periodo2: 'em julho',
  },
  {
    bem: 'o preço de tabela',
    sujeito: 'de um equipamento agrícola',
    periodo1: 'no primeiro reajuste',
    periodo2: 'no segundo',
  },
  {
    bem: 'o valor da diária',
    sujeito: 'de uma pousada',
    periodo1: 'na alta temporada',
    periodo2: 'na virada do ano',
  },
];

export interface Params {
  readonly cenario: Cenario;
  /** Valor inicial em reais, múltiplo de 400 — ver a nota sobre VALORES. */
  readonly valor: number;
  /** Taxas em pontos percentuais; negativas representam desconto. */
  readonly taxa1: number;
  readonly taxa2: number;
}

/**
 * O RESULTADO É SEMPRE UM NÚMERO INTEIRO DE REAIS, por construção.
 *
 * O item quer saber se o aluno entende que reajustes em cadeia se multiplicam. Se a
 * conta terminar em R$ 1.449,38, a dificuldade passa a vir da aritmética, e o item
 * mede outra coisa.
 *
 * A garantia é aritmética, não sorte: com o valor múltiplo de 400 e as taxas múltiplas
 * de 5, o produto
 *
 *     valor × (100 + k₁) × (100 + k₂) ÷ 10 000
 *
 * tem numerador divisível por 400 × 25 = 10 000 — porque (100 + k) é múltiplo de 5
 * sempre que k o for. Zero rejeição, zero dízima.
 */
const VALORES = [800, 1200, 1600, 2000, 2400, 2800, 3200] as const;
const TAXAS = [5, 10, 15, 20, 25] as const;

function drawParams(rng: Rng): Params {
  const cenario = rng.pick(CENARIOS);
  const valor = rng.pick(VALORES);
  const taxa1 = rng.pick(TAXAS);

  // Um dos dois pode ser desconto — variante mais difícil, porque quebra a intuição
  // de que "subiu 20% e caiu 20%" volta ao valor original.
  const segundaEDesconto = rng.bool(0.35);
  const bruta = rng.pick(TAXAS.filter((t) => t !== taxa1));
  const taxa2 = segundaEDesconto ? -bruta : bruta;

  return { cenario, valor, taxa1, taxa2 };
}

const fator = (k: number): number => 1 + k / 100;

/** A chave: os fatores incidem em cadeia, cada um sobre o valor já atualizado. */
function solveKey(p: Params): number {
  return roundTo(p.valor * fator(p.taxa1) * fator(p.taxa2), 2);
}

/**
 * NOTA SOBRE O CATÁLOGO, encontrada ao implementar.
 *
 * O catálogo lista, para H2.4.07, os distratores "soma os percentuais de reajuste" e
 * "aplica todos os reajustes sobre o valor inicial". Eles são raciocínios diferentes,
 * mas produzem O MESMO NÚMERO:
 *
 *     v·(1 + i₁ + i₂)   ≡   v + v·i₁ + v·i₂
 *
 * Não há como separá-los numa alternativa. Implementamos apenas o primeiro; se os dois
 * fossem escritos, o detector de colisão descartaria um deles de qualquer forma — o
 * que é o comportamento correto, mas gastaria uma vaga de distrator à toa.
 */
const BUGGY: readonly BuggySolver<Params, number>[] = [
  {
    // O erro que dá nome ao task-model.
    error: PCT_SOMA_TAXAS,
    priority: 1,
    solve: (p) => roundTo(p.valor * fator(p.taxa1 + p.taxa2), 2),
  },

  // --- faixa 2: sorteados entre si -----------------------------------------
  {
    error: PCT_SO_O_MAIOR,
    priority: 2,
    solve: (p) => {
      const maior = Math.abs(p.taxa1) >= Math.abs(p.taxa2) ? p.taxa1 : p.taxa2;
      return roundTo(p.valor * fator(maior), 2);
    },
  },
  {
    error: PCT_SO_O_PRIMEIRO,
    priority: 2,
    solve: (p) => {
      // Se a primeira taxa é a de maior módulo, este erro coincide com o anterior.
      if (Math.abs(p.taxa1) >= Math.abs(p.taxa2)) return NOT_APPLICABLE;
      return roundTo(p.valor * fator(p.taxa1), 2);
    },
  },
  {
    error: PCT_SINAL_TROCADO,
    priority: 2,
    solve: (p) => roundTo(p.valor * fator(p.taxa1) * fator(-p.taxa2), 2),
  },
  {
    // Substitui os dois reajustes por um único, igual à média deles.
    error: PCT_MEDIA_DAS_TAXAS,
    priority: 2,
    solve: (p) => {
      const media = (p.taxa1 + p.taxa2) / 2;
      // Com taxas simétricas a média é zero: o "reajuste" some e o distrator vira o
      // próprio valor inicial, que não é um erro plausível de cálculo.
      if (Math.abs(media) < 1e-9) return NOT_APPLICABLE;
      return roundTo(p.valor * fator(media), 2);
    },
  },
  {
    // Calculou certo, mas respondeu à pergunta errada: deu o aumento, não o total.
    error: PCT_VARIACAO_POR_MONTANTE,
    priority: 2,
    solve: (p) => roundTo(solveKey(p) - p.valor, 2),
  },

  // --- faixa 3: reposição ---------------------------------------------------
  {
    error: ARRED_TRUNCA,
    priority: 3,
    solve: (p) => {
      const k = solveKey(p);
      const t = Math.trunc(k);
      return Math.abs(k - t) < 1e-9 ? NOT_APPLICABLE : roundTo(t, 2);
    },
  },
];

const comSinal = (k: number): string =>
  k >= 0 ? `aumento de ${formatBR(k, 0)}%` : `desconto de ${formatBR(-k, 0)}%`;

// ---------------------------------------------------------------------------
// Explicações: a conta de cada erro, com os números desta variante
// ---------------------------------------------------------------------------

/** Com espaço inseparável: "R$" e o valor não se separam na quebra de linha. */
const reais = (x: number): string =>
  x < 0 ? `−R$\u00A0${formatBR(-x, 2)}` : `R$\u00A0${formatBR(x, 2)}`;

/** O fator como o livro escreve: "1,10", "0,85" — e "1,075" quando precisa. */
const fatorTxt = (k: number): string => formatBR(fator(k), Math.max(2, decimalPlaces(fator(k))));

/** A taxa com o sinal à vista: "10%", "−15%". */
const taxaTxt = (k: number): string => `${formatBR(k)}%`;

/** Duas taxas somadas por extenso: "10% + 5%", "10% − 15%". */
const somaTxt = (k1: number, k2: number): string =>
  `${taxaTxt(k1)} ${k2 < 0 ? '−' : '+'} ${formatBR(Math.abs(k2))}%`;

const EXPLICA: Explicacoes<Params, number> = {
  [PCT_SOMA_TAXAS]: (p, v) => [
    txt(
      `Você somou as duas taxas — ${somaTxt(p.taxa1, p.taxa2)} = ${taxaTxt(p.taxa1 + p.taxa2)} — ` +
        `e aplicou o resultado uma única vez sobre o valor inicial: ${reais(p.valor)} × ` +
        `${fatorTxt(p.taxa1 + p.taxa2)} = ${reais(v)}. `,
    ),
    txt(
      'Mas o segundo reajuste não incide sobre o valor inicial: incide sobre o valor que já ' +
        'tinha sido reajustado. Por isso os fatores se multiplicam, em vez de as taxas se somarem.',
    ),
  ],
  [PCT_SO_O_MAIOR]: (p, v) => {
    const primeiroMaior = Math.abs(p.taxa1) >= Math.abs(p.taxa2);
    const maior = primeiroMaior ? p.taxa1 : p.taxa2;
    const outro = primeiroMaior ? p.taxa2 : p.taxa1;
    return [
      txt(
        `Você aplicou só o maior dos reajustes, o ${comSinal(maior)}: ${reais(p.valor)} × ` +
          `${fatorTxt(maior)} = ${reais(v)}. `,
      ),
      txt(`Mas o outro, o ${comSinal(outro)}, também aconteceu — e muda o valor final.`),
    ];
  },
  [PCT_SO_O_PRIMEIRO]: (p, v) => [
    txt(
      `Você aplicou só o primeiro reajuste: ${reais(p.valor)} × ${fatorTxt(p.taxa1)} = ${reais(v)}. `,
    ),
    txt(
      `Esse é o valor no meio do caminho. Faltou aplicar sobre ele o segundo reajuste, ` +
        `o ${comSinal(p.taxa2)}.`,
    ),
  ],
  [PCT_SINAL_TROCADO]: (p, v) => [
    txt(
      `Você aplicou o segundo reajuste no sentido contrário — ` +
        `${p.taxa2 >= 0 ? 'como desconto, quando ele era um aumento' : 'como aumento, quando ele era um desconto'}: ` +
        `${reais(p.valor)} × ${fatorTxt(p.taxa1)} × ${fatorTxt(-p.taxa2)} = ${reais(v)}. `,
    ),
    txt(`O fator de um ${comSinal(p.taxa2)} é ${fatorTxt(p.taxa2)}.`),
  ],
  [PCT_MEDIA_DAS_TAXAS]: (p, v) => {
    const m = (p.taxa1 + p.taxa2) / 2;
    return [
      txt(
        `Você trocou os dois reajustes por um só, igual à média deles: ` +
          `(${somaTxt(p.taxa1, p.taxa2)}) ÷ 2 = ${taxaTxt(m)}. ` +
          `Aplicado uma vez, dá ${reais(p.valor)} × ${fatorTxt(m)} = ${reais(v)}. `,
      ),
      txt(
        'Dois reajustes seguidos não equivalem a um reajuste pela média das taxas: é preciso ' +
          'aplicar um e, sobre o resultado, o outro.',
      ),
    ];
  },
  [PCT_VARIACAO_POR_MONTANTE]: (p, v) => {
    const k = solveKey(p);
    return [
      txt(`Você calculou quanto o valor mudou: ${reais(k)} − ${reais(p.valor)} = ${reais(v)}. `),
      txt(
        `A conta estava certa até aí — mas a pergunta pede o valor final, ${reais(k)}, ` +
          'e não a diferença.',
      ),
    ];
  },
  [ARRED_TRUNCA]: (p, v) => [
    txt(
      `Você descartou os centavos em vez de arredondar: ${reais(v)} no lugar de ${reais(solveKey(p))}.`,
    ),
  ],
};

export const reajustesSucessivos: ItemTemplate<Params, number> = {
  id: templateId('h2-04-07-reajustes'),
  version: 2,
  taskModel: taskModelCode('H2.4.07'),
  skills: [skillCode('H2.4')],
  axis: 'II',
  level: 'N0',
  title: 'Reajustes percentuais sucessivos',
  // 2PL: sem alternativas, não há acerto ao acaso a estimar.
  responseModel: '2PL',

  difficultySources: ['steps.2'],

  params: drawParams,
  solve: solveKey,
  buggy: comExplicacoes(BUGGY, EXPLICA),

  /**
   * Separação mínima muito abaixo do padrão de 2% — e isso é deliberado.
   *
   * A regra dos 2% guarda contra duas ALTERNATIVAS NA TELA parecerem a mesma coisa.
   * Aqui não há alternativas: o aluno digita. O que precisa ser garantido é que dois
   * diagnósticos não se confundam, e a medida disso é `absTol` (meio centavo), não
   * uma fração da chave.
   *
   * A regra de 2% chegou a rejeitar 44% das seeds, sempre pelo mesmo motivo: com taxas
   * pequenas, "somar os percentuais" cai muito perto da resposta certa — R$ 1.130,00
   * contra R$ 1.134,00. Só que essa proximidade é justamente o que torna o erro
   * interessante, e num item de resposta digitada ela não atrapalha ninguém.
   */
  distractorMinRelSep: 0.0015,

  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    // Aumento seguido de desconto é bem mais difícil: quebra a intuição de que
    // "+20% e −20%" devolve o valor original.
    if (p.taxa2 < 0) fontes.push('inverse-problem');
    if (p.taxa1 % 10 !== 0 || Math.abs(p.taxa2) % 10 !== 0) fontes.push('non-round-numbers');
    return fontes;
  },

  build(p, key, distractors) {
    const { cenario } = p;

    return {
      // A frase de período já traz a própria preposição ("no primeiro ano", "em
      // janeiro"). Prefixá-la com "Em" produzia "Em no primeiro ano".
      stem: [
        txt(`${capitalizar(cenario.periodo1)}, ${cenario.bem} ${cenario.sujeito}, que era de `),
        strong(reais(p.valor)),
        txt(`, sofreu um ${comSinal(p.taxa1)}. `),
        txt(`Já ${cenario.periodo2}, sobre o valor então vigente, houve um `),
        txt(`${comSinal(p.taxa2)}.`),
      ],
      comando: [txt('Qual passou a ser o valor, em reais?')],
      // N0: nenhum estímulo. É de propósito — este é o piso da taxonomia.
      stimuli: [],
      interaction: {
        kind: 'numeric-entry',
        unit: 'reais',
        decimals: 2,
        keyValue: key,
        // Meio centavo: aceita a resposta certa escrita de qualquer forma razoável,
        // sem aceitar um valor genuinamente diferente.
        absTol: 0.005,
        // É isto que dá diagnóstico nomeado a uma resposta DIGITADA: a saída de cada
        // solucionador defeituoso continua sendo comparada com o que o aluno escreveu.
        errorValues: distractors.map((d) => ({
          value: d.value,
          error: d.error,
          ...(d.explanation ? { explanation: d.explanation } : {}),
        })),
      },
      rationale: [
        strong('Resolução. '),
        txt('Reajustes em cadeia se MULTIPLICAM; cada um incide sobre o valor já atualizado.'),
        br,
        txt(
          `Após o primeiro: ${formatBR(p.valor, 2)} × ${formatBR(fator(p.taxa1), 2)} = ` +
            `${formatBR(roundTo(p.valor * fator(p.taxa1), 2), 2)}`,
        ),
        br,
        txt(
          `Após o segundo: ${formatBR(roundTo(p.valor * fator(p.taxa1), 2), 2)} × ` +
            `${formatBR(fator(p.taxa2), 2)} = ${formatBR(key, 2)}`,
        ),
        br,
        txt(
          `Somar as taxas daria ${formatBR(roundTo(p.valor * fator(p.taxa1 + p.taxa2), 2), 2)} — ` +
            'valor diferente, porque ignora que o segundo reajuste incide sobre uma base já alterada.',
        ),
      ],
      declared: p.taxa2 < 0 ? 'dificil' : 'media',
    };
  },

  validate(built, p) {
    const k = solveKey(p);
    if (!(k > 0)) return `valor final não positivo: ${k}`;

    // A guarda que substitui a regra dos 2%: baixar a separação relativa só é seguro
    // enquanto NENHUM diagnóstico ficar ambíguo. Dois valores dentro da tolerância um
    // do outro fariam a mesma resposta digitada casar com dois erros diferentes.
    if (built.interaction.kind === 'numeric-entry') {
      const inter = built.interaction;
      const todos = [k, ...inter.errorValues.map((e) => e.value)];
      for (let i = 0; i < todos.length; i++) {
        for (let j = i + 1; j < todos.length; j++) {
          const a = todos[i] as number;
          const b = todos[j] as number;
          if (Math.abs(a - b) <= 2 * inter.absTol) {
            return `diagnóstico ambíguo: ${a} e ${b} estão a menos de 2×absTol`;
          }
        }
      }
    }
    // O resultado inteiro é premissa da amostragem construtiva; se falhar, o
    // gerador mudou e a validação precisa saber antes do aluno.
    if (Math.abs(k - Math.round(k)) > 1e-9) {
      return `valor final ${k} não é um número inteiro de reais`;
    }
    return null;
  },
};
